import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { MessageCircle, RefreshCw, AlertTriangle } from "lucide-react";

const KIND_LABEL = {
  driver_new_order: "Driver · New Order",
  customer_driver_location: "Customer · Driver Location",
  customer_delivered: "Customer · Delivered",
  driver_delivered: "Driver · Delivered",
};

export default function WhatsAppLog() {
  const [data, setData] = useState({ messages: [], dev_mode: true, configured: false });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data: r } = await api.get("/admin/whatsapp-log");
      setData(r);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="card-industrial p-6 md:p-8" data-testid="whatsapp-log">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-mono-num text-[10px] uppercase tracking-widest text-yellow-400">// Comms</div>
          <h3 className="font-display text-3xl font-black uppercase mt-1">WhatsApp log</h3>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={`font-mono-num text-[10px] uppercase tracking-widest px-2 py-1 border ${data.dev_mode ? "border-yellow-400/50 text-yellow-400 bg-yellow-400/5" : "border-emerald-800 text-emerald-300 bg-emerald-950/30"}`}
            data-testid="whatsapp-mode-badge"
          >
            {data.dev_mode ? "DEV MODE · not sending" : "LIVE"}
          </span>
          <button onClick={load} className="btn-ghost px-2 py-1 text-[10px]" data-testid="whatsapp-refresh-btn">
            <RefreshCw className="w-3 h-3" /> REFRESH
          </button>
        </div>
      </div>

      {data.dev_mode && (
        <div className="mb-5 flex items-start gap-3 border border-zinc-800 bg-[#0a0a0a] p-3 text-zinc-400">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-yellow-400" />
          <div className="text-xs">
            Messages are being <b>logged only</b>. Add your Twilio credentials
            (<code className="font-mono-num text-yellow-400">TWILIO_ACCOUNT_SID</code>,
            <code className="font-mono-num text-yellow-400"> TWILIO_AUTH_TOKEN</code>,
            <code className="font-mono-num text-yellow-400"> TWILIO_WHATSAPP_FROM</code>)
            and set <code className="font-mono-num text-yellow-400">WHATSAPP_DEV_MODE=false</code> to go live.
          </div>
        </div>
      )}

      {loading ? (
        <div className="p-6 text-center font-mono-num text-xs uppercase tracking-widest text-zinc-500">Loading…</div>
      ) : data.messages.length === 0 ? (
        <div className="p-8 text-center border border-dashed border-zinc-800">
          <MessageCircle className="w-8 h-8 mx-auto text-zinc-600" />
          <div className="mt-3 font-display uppercase text-lg text-zinc-400">No messages yet</div>
          <div className="font-mono-num text-[11px] uppercase tracking-widest text-zinc-600 mt-1">
            WhatsApp is sent on new orders, en-route, and delivered.
          </div>
        </div>
      ) : (
        <div className="space-y-3 max-h-[420px] overflow-y-auto pr-2">
          {data.messages.map((m) => (
            <div key={m.id} className="border border-zinc-800 bg-[#0a0a0a] p-4" data-testid={`wa-msg-${m.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="font-mono-num text-[10px] uppercase tracking-widest text-yellow-400">
                  {KIND_LABEL[m.kind] || m.kind}
                </div>
                <div className="font-mono-num text-[10px] uppercase tracking-widest text-zinc-500">
                  {new Date(m.sent_at).toLocaleString()}
                </div>
              </div>
              <div className="font-mono-num text-xs text-zinc-500 mb-2">→ {m.to}</div>
              <pre className="text-sm text-zinc-200 whitespace-pre-wrap font-sans leading-relaxed">{m.message}</pre>
              <div className="mt-2 font-mono-num text-[10px] uppercase tracking-widest">
                {m.status === "sent" && <span className="text-emerald-400">SENT · {m.twilio_sid}</span>}
                {m.status === "logged" && <span className="text-yellow-400">LOGGED (dev)</span>}
                {m.status === "error" && <span className="text-red-400">ERROR · {m.error}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
